import type { DocPageContent } from '../docs-content'

const page = {
  order: 15.5,
  slug: 'motion-library',
  title: '应用并复用动画',
  description:
    '应用可编辑预设、添加实时行为、调整文本运动，并保存动画以便复用。',
  category: '创意工具',
  related: ['motion', 'keyframes', 'properties', 'text-captions-subtitles'],
  sections: [
    {
      title: '选择正确的动画界面',
      blocks: [
        {
          kind: 'paragraph',
          text: '精剪在两种位置使用同一套动画系统。编辑序列片段时使用紧凑的**动画**标签页，或进行分层合成工作时打开**运动**。',
        },
        {
          kind: 'table',
          headers: ['界面', '用途'],
          rows: [
            [
              '编辑 > 动画',
              '常用预设、已应用动画状态、文本运动，以及创建或重新打开运动片段',
            ],
            [
              '运动 > 运动库',
              '完整的预设目录、实时行为、已保存动画，以及所选图层的控件',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '选中一个或多个编辑片段，并选择**创建运动片段**，可将其保留在分层合成体内。选中已有的运动片段并选择**在运动中打开**，可编辑其图层。',
        },
      ],
    },
    {
      title: '应用可编辑预设',
      blocks: [
        {
          kind: 'steps',
          items: [
            '选中一个或多个片段或运动图层。',
            '在**入场**、**出场**或**强调**下选择一个预设。',
            '将**应用时**设为**替换**，以相同意图交换运动；或设为**添加**以保留现有关键帧。',
            '打开属性行或数值曲线图，调整生成的关键帧。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '**已应用**部分会分别列出手动关键帧与每次预设应用。选中某项可将播放头移动到其第一个关键帧。',
        },
      ],
    },
    {
      title: '添加实时行为',
      blocks: [
        {
          kind: 'paragraph',
          text: '实时行为会在播放期间求值，而无需创建关键帧。保留其“实时”状态可获得可调的运动，或在需要逐帧控制时将其烘焙。',
        },
        {
          kind: 'table',
          headers: ['行为', '效果'],
          rows: [
            ['漂浮漂移', '偏移位置与旋转'],
            ['摇摆', '来回旋转'],
            ['呼吸脉动', '改变大小与不透明度'],
            ['旋转', '持续旋转'],
            ['微抖动', '添加带随机种子的位置与旋转抖动'],
          ],
        },
        {
          kind: 'list',
          items: [
            '选择一个行为方块以默认设置应用它。',
            '在其应用后的控件中调整**强度**与**时长**。',
            '在运动时间轴中查看阴影条带，以了解程序化运动的运行区间。',
            '将某个行为应用到多个片段，以错开它们的相位，而不是让所有片段步调一致地移动。',
          ],
        },
      ],
    },
    {
      title: '在时间轴上调整文本运动',
      blocks: [
        {
          kind: 'paragraph',
          text: '文本运动通过实时的**入**、**出**与**循环**槽位，为字符、单词、行或整个片段制作动画。',
        },
        {
          kind: 'list',
          items: [
            '选中一个文本片段，然后在**动画**标签页或运动库中为各个槽位选择预设。',
            '在运动中拖动文本运动带的边缘以更改其时长。',
            '将**入**或**出**带拖离片段边缘，以偏移其起点或终点。',
            '选中其**已应用**项可将播放头移动到该带。',
          ],
        },
      ],
    },
    {
      title: '检查并清理已应用的动画',
      blocks: [
        {
          kind: 'list',
          items: [
            '使用**已应用**来区分手动关键帧、生成的关键帧、实时行为与文本运动。',
            '移除某一次预设应用，而不删除无关的关键帧。',
            '从其所在行移除某个实时行为或文本运动槽位。',
            '当修剪导致关键帧落在片段之外时，使用**修剪动画**仅删除那些滞留的关键帧。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '**修剪动画**会改变已存储的动画。如果需要恢复被移除的关键帧，请使用提示条中的操作或撤销。',
        },
      ],
    },
    {
      title: '烘焙、保存与复用动画',
      blocks: [
        {
          kind: 'steps',
          items: [
            '选择**烘焙为关键帧**，将实时图层行为采样为可编辑的属性关键帧。',
            '选中一个包含你想保留的动画的片段。',
            '选择**保存动画**，输入名称，并将其保存到项目库中。',
            '将已保存的动画应用到另一个兼容的片段或图层。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '已保存的动画可以包含可编辑关键帧、实时图层行为、文本运动，或其组合。导出的项目会携带其已保存的动画库。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
